import { useEffect, useState } from "react";
import { BudgetBar } from "@core/components/List";
import { Stepper } from "@core/components/Stepper";
import { Value } from "@core/components/Value";
import { ChosenRow, ModuleCards } from "./parts";
import { page, signed, useSm, type RaceEntry } from "./view";

export function IdeaStep() {
  const { view, apply } = useSm();
  const [concept, setConcept] = useState(view.concept);
  return (
    <section className="card stack">
      <label className="field">
        <span>Idee und Konzept (nur für dich, ohne Regelwirkung)</span>
        <textarea
          rows={6}
          value={concept}
          placeholder="Was für ein Abenteurer soll es werden? Herkunft, Beruf, Eigenheiten, Ziele …"
          onChange={(e) => setConcept(e.target.value)}
          onBlur={() => concept !== view.concept && void apply({ type: "sm.setConcept", payload: { text: concept } })}
        />
      </label>
      <p className="muted small">
        Lass dich von Rassen, Kulturen, Abstammungen und Ausbildungen inspirieren. Die 16 Fragen auf GRW S. 29–30 helfen beim Hintergrund. Alles Weitere ergibt sich
        Schritt für Schritt.
      </p>
    </section>
  );
}

export function RaceStep() {
  const { view, confirm } = useSm();
  const races = view.catalog?.races ?? [];
  const short = (id: string) => view.attributes.find((x) => x.id === id)?.short ?? id;
  const chosen = view.race ? races.find((r) => r.id === view.race?.id) : undefined;
  const [choosing, setChoosing] = useState(!view.race);
  // Once a race is set (also after a change), the cards give way to the summary row so the open choices below are in view.
  useEffect(() => setChoosing(!view.race), [view.race?.id]);
  const attributes = (race: RaceEntry) =>
    `${Object.entries(race.fixed)
      .map(([a, v]) => `${short(a)} ${signed(v)}`)
      .join(", ") || "–"}${race.free > 0 ? `, ${race.free}× +1 frei` : ""}`;

  if (chosen && !choosing) {
    return (
      <ChosenRow
        kicker="Rasse"
        source={page(chosen.source)}
        title={chosen.name}
        description={chosen.description}
        facts={`GK ${chosen.sizeClass} · ${attributes(chosen)} · ${chosen.strengths.join(", ") || "keine Stärken"}`}
        onChange={() => setChoosing(true)}
      />
    );
  }

  return (
    <div className="stack">
      {chosen && (
        <div className="row spread">
          <span className="muted small">Gewählt ist {chosen.name}. Eine andere Rasse ersetzt sie samt freiem Attributsbonus.</span>
          <button type="button" className="link" onClick={() => setChoosing(false)}>
            Abbrechen
          </button>
        </div>
      )}
      <ModuleCards
        entries={races}
        selectedId={view.race?.id}
        details={(entry) => {
          const race = entry as RaceEntry;
          return (
            <dl className="facts">
              <dt>Größenklasse</dt>
              <dd>{race.sizeClass}</dd>
              <dt>Attribute</dt>
              <dd>{attributes(race)}</dd>
              <dt>Stärken</dt>
              <dd>{race.strengths.join(", ") || "–"}</dd>
            </dl>
          );
        }}
        onPick={(race) => {
          window.scrollTo({ top: 0 });
          confirm({ type: "sm.setRace", payload: { race: race.id } }, `Rasse ${race.name}`);
        }}
      />
    </div>
  );
}

export function AttributesStep() {
  const { view, apply, budget } = useSm();
  const rules = view.catalog?.attributeRules ?? { points: 18, min: 1, max: 3 };
  const left = budget(view.budgets.attributes);
  const used = rules.points - left;
  return (
    <section className="card stack">
      <BudgetBar
        label="Attributpunkte"
        used={used}
        total={rules.points}
        hint={`Je Attribut ${rules.min} bis ${rules.max} Punkte. Die Rassenmodifikatoren und der freie Bonus kommen danach hinzu; der Startwert ist das Ergebnis.`}
      />
      <table className="attribute-table">
        <thead>
          <tr>
            <th>Attribut</th>
            <th>Punkte</th>
            <th>Rasse</th>
            <th>Start</th>
          </tr>
        </thead>
        <tbody>
          {view.attributes.map((a) => {
            const raceMod = view.race ? (view.catalog?.races.find((r) => r.id === view.race?.id)?.fixed[a.id] ?? 0) : 0;
            return (
              <tr key={a.id}>
                <td>
                  {a.name} <span className="muted">{a.short}</span>
                </td>
                <td>
                  <Stepper label={a.name} value={a.points} min={Math.min(a.points, rules.min)} max={rules.max} onChange={(points) => apply({ type: "sm.setAttribute", payload: { attribute: a.id, points } })} />
                </td>
                <td className="muted">{raceMod ? signed(raceMod) : "–"}</td>
                <td>
                  <Value valueKey={a.start} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted small">Der Startwert enthält auch den freien Rassenbonus aus Schritt 2. Antippen zeigt die Herleitung.</p>
    </section>
  );
}
